/**
 * Client for the Go transit service (overmatch/go), proxied at /api/transit.
 *
 * Transit signs its own users in (not Django). The access token lives only in
 * memory; the refresh token is an HttpOnly cookie scoped to /api/transit/auth
 * that scripts never see. A page load, or a 401, refreshes once through the
 * cookie and retries; when that fails the user signs in again.
 */
import type {
  Policy,
  DraftBlock,
  GraphNode,
  Readiness,
  DocumentScan,
  DocumentType,
  DocumentVersion,
  InfoRequest,
  Invite,
  InviteLink,
  Org,
  Product,
  TransitDocument,
  Transition,
  TransitionGraph,
  TransitRole,
  TransitUser,
  Vendor,
} from '../types/transit'
import { isSampleId, isSamplePath, SAMPLE_READ_ONLY, sampleResponse } from '../components/transit/sample/api'

const BASE = `${import.meta.env.VITE_API_URL || ''}/api/transit`

let accessToken: string | null = null
let refreshing: Promise<boolean> | null = null

type SessionReply = { access_token: string; expires_in: number; user: TransitUser }

async function startSession(res: Response): Promise<TransitUser> {
  if (!res.ok) throw await failure(res)
  const s = (await res.json()) as SessionReply
  accessToken = s.access_token
  return s.user
}

/** Trades the refresh cookie for a new access token. One at a time: each
 * refresh rotates the cookie, so concurrent calls share the one in flight. */
function refresh(): Promise<boolean> {
  refreshing ??= fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'X-Requested-With': 'transit' },
    credentials: 'same-origin',
  })
    .then(async (res) => {
      if (!res.ok) {
        accessToken = null
        return false
      }
      await startSession(res)
      return true
    })
    .catch(() => false)
    .finally(() => { refreshing = null })
  return refreshing
}

/** fetch with the access token, refreshing it once (before, if there is none; or on a 401).
 * The sample transition (components/transit/sample) is answered here, never sent. */
async function send(path: string, init: RequestInit = {}): Promise<Response> {
  if (isSamplePath(path)) return sampleResponse(path, init)
  const go = () => {
    const headers: Record<string, string> = { ...(init.headers as Record<string, string>) }
    if (!(init.body instanceof FormData) && init.body !== undefined) headers['Content-Type'] = 'application/json'
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    return fetch(`${BASE}${path}`, { ...init, headers, cache: 'no-store' })
  }
  const refreshed = !accessToken && (await refresh())
  if (!accessToken) return go() // no session: the 401 sends the user to sign in
  const res = await go()
  if (res.status !== 401 || refreshed) return res
  return (await refresh()) ? go() : res
}

const authPost = (path: string, body: unknown) =>
  fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'transit' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  })

/** Sign-in, links and sign-out: none of these need an access token. */
export const transitAuth = {
  signIn: async (email: string, password: string) => startSession(await authPost('/auth/login', { email, password })),
  openLink: async (token: string) => {
    const res = await fetch(`${BASE}/auth/invites/${encodeURIComponent(token)}`)
    if (!res.ok) throw await failure(res)
    return (await res.json()) as InviteLink
  },
  acceptLink: async (token: string, name: string, password: string) =>
    startSession(await authPost(`/auth/invites/${encodeURIComponent(token)}/accept`, { name, password })),
  signOut: async () => {
    accessToken = null
    await authPost('/auth/logout', {}).catch(() => undefined)
  },
}

/** The reply's error message, with its status (401: sign in again). */
export class TransitError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
  }
}

async function failure(res: Response): Promise<TransitError> {
  const body = await res.json().catch(() => null)
  return new TransitError(body?.error || `HTTP ${res.status}`, res.status)
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await send(path, init)
  if (!res.ok) throw await failure(res)
  return res.json() as Promise<T>
}

const post = <T,>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) })

export const transitApi = {
  me: () => request<{ user: TransitUser; org: Org }>('/me'),

  // The org's people (admins only).
  listUsers: () => request<TransitUser[]>('/users'),
  updateUser: (id: string, role: TransitRole, disabled: boolean) =>
    request<TransitUser>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify({ role, disabled }) }),
  resetLink: (id: string) => post<{ token: string; invite: Invite }>(`/users/${id}/reset`, {}),
  listInvites: () => request<Invite[]>('/invites'),
  createInvite: (email: string, role: TransitRole) => post<{ token: string; invite: Invite }>('/invites', { email, role }),
  revokeInvite: async (id: string) => {
    const res = await send(`/invites/${id}`, { method: 'DELETE' })
    if (!res.ok) throw await failure(res)
  },

  listVendors: () => request<Vendor[]>('/vendors'),
  createVendor: (name: string) => post<Vendor>('/vendors', { name }),
  listProducts: (vendorId: string) => request<Product[]>(`/vendors/${vendorId}/products`),
  createProduct: (vendorId: string, name: string) => post<Product>(`/vendors/${vendorId}/products`, { name }),

  listTransitions: () => request<Transition[]>('/transitions'),
  createTransition: (productId: string, name: string) =>
    post<Transition>('/transitions', { product_id: productId, name }),
  getTransition: (id: string) => request<Transition>(`/transitions/${id}`),
  getGraph: (id: string) => request<TransitionGraph>(`/transitions/${id}/graph`),

  listDocuments: (id: string) => request<TransitDocument[]>(`/transitions/${id}/documents`),
  uploadDocument: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<TransitDocument>(`/transitions/${id}/documents`, { method: 'POST', body: form })
  },

  /** Reads one file and names its type, vendor and product; stores nothing. 422: unreadable file. */
  scanDocument: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<DocumentScan>('/documents/scan', { method: 'POST', body: form })
  },

  addWrittenInput: (id: string, title: string, text: string) =>
    post<TransitDocument>(`/transitions/${id}/documents/written`, { title, text }),
  setDocumentType: (id: string, documentType: string | null) =>
    request<TransitDocument>(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify({ document_type: documentType }) }),
  listDocumentTypes: () => request<DocumentType[]>('/document-types'),

  documentText: (id: string) => request<{ text: string }>(`/documents/${id}/text`),
  /** Remove an input document and its file (not the generated package). */
  deleteDocument: async (id: string) => {
    const res = await send(`/documents/${id}`, { method: 'DELETE' })
    if (!res.ok) throw await failure(res)
  },

  draftReady: (id: string) => post<{ queued: string[] }>(`/transitions/${id}/draft`, {}),
  approveNode: (id: string, key: string) => post<{ queued: string[] }>(`/transitions/${id}/nodes/${key}/approve`, {}),
  redraftNode: (id: string, key: string) => post<{ queued: string[] }>(`/transitions/${id}/nodes/${key}/draft`, {}),

  editNode: (id: string, key: string, revision: number, blocks: DraftBlock[]) =>
    request<GraphNode>(`/transitions/${id}/nodes/${key}`, { method: 'PATCH', body: JSON.stringify({ revision, blocks }) }),
  confirmRow: (id: string, key: string, revision: number, block: number, row: number, confirmed: boolean) =>
    post<GraphNode>(`/transitions/${id}/nodes/${key}/confirm`, { revision, block, row, confirmed }),

  listPolicies: (id: string) => request<Policy[]>(`/transitions/${id}/policies`),
  uploadPolicy: (id: string, file: File, nodeKey: string, orgWide: boolean) => {
    // An org-wide policy's path names no transition, so the sample can't catch it.
    if (isSampleId(id)) return Promise.reject(new TransitError(SAMPLE_READ_ONLY, 403))
    const form = new FormData()
    form.append('file', file)
    if (nodeKey) form.append('node_key', nodeKey)
    return request<Policy>(orgWide ? '/policies' : `/transitions/${id}/policies`, { method: 'POST', body: form })
  },
  deletePolicy: async (policyId: string) => {
    const res = await send(`/policies/${policyId}`, { method: 'DELETE' })
    if (!res.ok) throw await failure(res)
  },

  getReadiness: (id: string) => request<Readiness>(`/transitions/${id}/readiness`),
  assessReadiness: (id: string) => post<{ queued: boolean }>(`/transitions/${id}/readiness`, {}),
  recordReadinessEvent: (id: string, event: string, note: string) => post(`/transitions/${id}/readiness/events`, { event, note }),

  listPackageVersions: (id: string) => request<DocumentVersion[]>(`/transitions/${id}/package`),
  generatePackage: (id: string) => post<DocumentVersion>(`/transitions/${id}/package`, {}),
  /** Fetches a version's .docx (the auth header rules out a plain link) and saves it. */
  downloadPackage: async (id: string, version: number) => {
    const res = await send(`/transitions/${id}/package/${version}`)
    if (!res.ok) throw await failure(res)
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? `package-v${version}.docx`
    const url = URL.createObjectURL(await res.blob())
    const a = document.createElement('a')
    a.href = url
    a.download = name
    a.click()
    URL.revokeObjectURL(url)
  },

  listInfoRequests: (id: string) => request<InfoRequest[]>(`/transitions/${id}/info-requests`),
  answerInfoRequest: (id: string, answer: string) => post<InfoRequest>(`/info-requests/${id}/answer`, { answer }),
  dismissInfoRequest: (id: string) => post<InfoRequest>(`/info-requests/${id}/dismiss`, {}),
}
